import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api";
import { setActivePropertyCode } from '../auth/userSlice';

export const fetchProperties = createAsyncThunk(
  "company/fetchProperties",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/company/properties/?ordering=id");
      return response.data.results;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Errore nel caricamento delle proprietà");
    }
  }
);

export const fetchActiveProperty = createAsyncThunk(
  "company/fetchActiveProperty",
  async (code, { dispatch, rejectWithValue }) => { 
    if (!code) return rejectWithValue("Nessun property code attivo");
    try {
      const response = await api.get(`/company/properties/${encodeURIComponent(code)}/`);
      dispatch(setActivePropertyCode(code));
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Errore nel caricamento della proprietà attiva");
    }
  }
);

export const upsertProperty = createAsyncThunk(
  "company/upsertProperty",
  async (propertyData, { rejectWithValue }) => {
    try {
      const response = await api.post("/company/properties/", propertyData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Errore nel salvataggio della proprietà");
    }
  }
);

export const updateEnterprise = createAsyncThunk(
  "company/updateEnterprise",
  async ({ code, formData }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/company/enterprises/${code}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Errore nell'aggiornamento dell'azienda");
    }
  }
);

const initialState = {
  properties: [],
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  activeProperty: null,
  activeStatus: "idle",
  activeError: null,
};

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProperties.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProperties.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.properties = action.payload;
      })
      .addCase(fetchProperties.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(upsertProperty.fulfilled, (state, action) => {
        const updatedProperty = action.payload;
        const index = state.properties.findIndex(p => p.id === updatedProperty.id);

        if (index !== -1) {
          state.properties[index] = updatedProperty;
        } else {
          state.properties.push(updatedProperty);
        }

        // Se la proprietà aggiornata è quella attiva, aggiorna anche activeProperty
        if (state.activeProperty && state.activeProperty.id === updatedProperty.id) {
            state.activeProperty = updatedProperty;
        }
      })
      .addCase(updateEnterprise.fulfilled, (state, action) => {
        const updatedEnterprise = action.payload;

        // 1. Aggiorna l'enterprise in ogni proprietà nella lista principale
        state.properties = state.properties.map(prop => 
          prop.enterprise?.id === updatedEnterprise.id
            ? { ...prop, enterprise: updatedEnterprise }
            : prop
        );

        // 2. Aggiorna l'enterprise nella proprietà attiva, se esiste e corrisponde
        if (state.activeProperty && state.activeProperty.enterprise?.id === updatedEnterprise.id) {
          state.activeProperty = { ...state.activeProperty, enterprise: updatedEnterprise };
        }
      })
      .addCase(fetchActiveProperty.pending, (state) => {
        state.activeStatus = "loading";
        state.activeError = null;
      })
      .addCase(fetchActiveProperty.fulfilled, (state, action) => {
        state.activeStatus = "succeeded";
        state.activeProperty = action.payload;
      })
      .addCase(fetchActiveProperty.rejected, (state, action) => {
        state.activeStatus = "failed";
        state.activeError = action.payload;
        state.activeProperty = null;
      });
  },
});

export default companySlice.reducer;